import React, { useState, useRef, useEffect } from 'react'
import { DayPicker } from 'react-day-picker'
import { es } from 'date-fns/locale'
import { format, parse, isValid } from 'date-fns'
import * as Popover from '@radix-ui/react-popover'
import { Calendar } from 'lucide-react'
import 'react-day-picker/style.css'

interface DatePickerProps {
  value?: string
  onChange?: (val: string) => void
  name?: string
  onBlur?: () => void
  placeholder?: string
  className?: string
  disabled?: boolean
  required?: boolean
  min?: string
  max?: string
  id?: string
  'aria-label'?: string
  'aria-labelledby'?: string
  /** Para que un campo pueda enlazar su mensaje de error o su hint. */
  'aria-describedby'?: string
}

const DatePicker = React.forwardRef<HTMLInputElement, DatePickerProps>(
  ({ value = '', onChange, name, onBlur, placeholder = 'dd-mm-aaaa', className, disabled, required, min, max, id, 'aria-label': ariaLabel, 'aria-labelledby': ariaLabelledBy, 'aria-describedby': ariaDescribedBy }, ref) => {
    const [open, setOpen] = useState(false)
    const [inputValue, setInputValue] = useState('')
    const internalRef = useRef<HTMLInputElement>(null)

    // Sync inputValue when value prop changes
    useEffect(() => {
      if (value) {
        const d = parse(value, 'yyyy-MM-dd', new Date())
        if (isValid(d)) {
          setInputValue(format(d, 'dd-MM-yyyy'))
        } else {
          setInputValue(value)
        }
      } else {
        setInputValue('')
      }
    }, [value])

    const selectedDate = value ? parse(value, 'yyyy-MM-dd', new Date()) : undefined
    const validSelected = selectedDate && isValid(selectedDate) ? selectedDate : undefined

    const minDate = min ? parse(min, 'yyyy-MM-dd', new Date()) : undefined
    const maxDate = max ? parse(max, 'yyyy-MM-dd', new Date()) : undefined

    const handleDaySelect = (day: Date | undefined) => {
      if (day) {
        const iso = format(day, 'yyyy-MM-dd')
        onChange?.(iso)
        setOpen(false)
      }
    }

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value
      setInputValue(raw)

      // Try parsing dd-MM-yyyy
      if (raw.length === 10) {
        const d = parse(raw, 'dd-MM-yyyy', new Date())
        if (isValid(d)) {
          onChange?.(format(d, 'yyyy-MM-dd'))
          return
        }
      }
      // If cleared
      if (!raw) {
        onChange?.('')
      }
    }

    const handleInputBlur = () => {
      onBlur?.()
      // Re-sync display if user typed something invalid
      if (inputValue && value) {
        const d = parse(value, 'yyyy-MM-dd', new Date())
        if (isValid(d)) {
          setInputValue(format(d, 'dd-MM-yyyy'))
        }
      }
    }

    const disabledMatcher = (date: Date) => {
      if (minDate && isValid(minDate) && date < minDate) return true
      if (maxDate && isValid(maxDate) && date > maxDate) return true
      return false
    }

    return (
      <Popover.Root open={open} onOpenChange={setOpen}>
        <div className="relative">
          <Popover.Trigger asChild>
            <button
              type="button"
              disabled={disabled}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-content-subtle hover:text-content-muted focus:outline-none"
              tabIndex={-1}
            >
              <Calendar className="w-4 h-4" />
            </button>
          </Popover.Trigger>
          <input
            ref={ref || internalRef}
            id={id}
            type="text"
            name={name}
            value={inputValue}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            placeholder={placeholder}
            disabled={disabled}
            required={required}
            aria-label={ariaLabel}
            aria-labelledby={ariaLabelledBy}
            aria-describedby={ariaDescribedBy}
            className={`${className || "w-full border border-input-border rounded-lg px-3 py-2 text-sm bg-input-bg text-content focus:outline-none focus:ring-2 focus:ring-primary disabled:bg-surface-muted disabled:text-content-subtle"} pr-8`}
          />
          {/* z-[100]: por ENCIMA del modal del design system (.cl-modal / .cl-scrim,
              z-index 80). Con z-50 el calendario se abría detrás de todo modal
              desde el release #1365 (lo vigila capasFlotantes.test.ts). */}
          <Popover.Portal>
            <Popover.Content
              align="start"
              sideOffset={4}
              className="z-[100] bg-popover rounded-xl shadow-lg border border-divider p-3 animate-in fade-in-0 zoom-in-95"
              style={{ backgroundColor: 'rgb(var(--color-popover))' }}
            >
              <DayPicker
                mode="single"
                selected={validSelected}
                onSelect={handleDaySelect}
                defaultMonth={validSelected || new Date()}
                locale={es}
                disabled={disabledMatcher}
                captionLayout="dropdown"
                startMonth={minDate && isValid(minDate) ? minDate : new Date(2010, 0)}
                endMonth={maxDate && isValid(maxDate) ? maxDate : new Date(new Date().getFullYear() + 5, 11)}
                classNames={{
                  root: 'text-sm',
                  months: 'flex flex-col',
                  // 🔴 `px-7` reserva el ancho de las dos flechas, que van
                  // `absolute` (ver `nav`) y por lo tanto NO ocupan lugar en el
                  // flujo del caption. Sin esa reserva se superponen a los
                  // selectores en cuanto el nombre del mes es largo: medido con
                  // «Septiembre» —el más largo del año en español— los dropdowns
                  // ocupaban 201,5px de los 226px utiles, dejando 12,2px por
                  // lado para flechas de 24px, o sea ~12px de solape a cada
                  // lado. El calendario queda un poco más ancho que la grilla de
                  // días (7 × 32px), y por eso los días se centran abajo.
                  month_caption: 'flex justify-center items-center h-8 px-9 font-semibold text-content capitalize',
                  dropdowns: 'flex gap-2 items-center justify-center h-8 text-sm',
                  // El <select> nativo va invisible (opacity-0) sobre el label
                  // estilizado, PERO su lista de <option> nativa hereda su
                  // background-color y color. Con bg-transparent, en modo oscuro
                  // las opciones quedaban texto claro sobre fondo claro (ilegible).
                  // Fijamos fondo y texto sólidos del tema para asegurar contraste.
                  dropdown: 'absolute inset-0 w-full h-full opacity-0 cursor-pointer appearance-none border-0 bg-surface text-content [color-scheme:light] dark:[color-scheme:dark]',
                  dropdown_root: 'relative inline-flex items-center gap-1 bg-surface border border-divider rounded px-2 py-1 text-content text-sm focus-within:ring-2 focus-within:ring-primary',
                  caption_label: 'inline-flex items-center gap-1 capitalize pointer-events-none',
                  months_dropdown: 'capitalize',
                  years_dropdown: '',
                  nav: 'flex items-center justify-between absolute top-3 left-3 right-3',
                  button_previous: 'p-1 rounded-md hover:bg-soft text-content-muted',
                  button_next: 'p-1 rounded-md hover:bg-soft text-content-muted',
                  weekdays: 'flex justify-center',
                  weekday: 'w-8 text-center text-xs font-medium text-content-subtle capitalize',
                  week: 'flex justify-center',
                  day: 'w-8 h-8 flex items-center justify-center text-sm rounded-md hover:bg-primary-50 cursor-pointer transition-colors',
                  day_button: 'w-full h-full flex items-center justify-center rounded-md',
                  selected: 'bg-primary text-on-primary hover:bg-primary-hover font-semibold',
                  today: 'font-bold text-primary',
                  outside: 'text-content-subtle',
                  disabled: 'text-content-subtle cursor-not-allowed hover:bg-transparent',
                }}
              />
            </Popover.Content>
          </Popover.Portal>
        </div>
      </Popover.Root>
    )
  }
)

DatePicker.displayName = 'DatePicker'

export default DatePicker
